/* The quiz funnel (sections/st-funnel.liquid). One question per screen; the rules that turn answers
   into setups are in assets/st-funnel-rules.js.

   - Steps: shop route audience, platforms, spots, size, result; volume route audience, locations,
     formats, platforms, units, design, volume. A size of 100 or more moves to the volume route.
   - A tap on a single-choice answer moves on by itself; a keyboard change waits for Next, so arrow
     keys can move through the choices. Each step is a history entry, so the phone's back gesture
     goes to the previous question, not out of the page. Answers are kept in sessionStorage.
   - "Get this setup" adds the packs with Shopify's cart API, writes hidden cart attributes (so the
     order shows it came from the quiz even when every pixel is blocked), applies the setup's code
     and posts to checkout, as the cart page's own button does.

   Events, through Shopify's customer events (Shopify.analytics.publish), which apply the visitor's
   cookie choice; the Upstack Data and Smartap events pixels forward them (TRACKING.md):
     st_funnel_view      the page opened (niche, utm)
     st_funnel_start     the first answer
     st_funnel_step      each answer (step, answer)
     st_funnel_result    the result shown (route, setups and values), again only if the answers changed
     st_funnel_setup     a setup picked, or the metal upgrade switched
     st_funnel_product   a product in a setup tapped (product, variant, price)
     st_funnel_pack      a pack size picked (variant, pack, value)
     st_funnel_checkout  "Get this setup" or "Buy now" pressed (tier, value, code); the cart's earlier
                         funnel lines (line property _st_funnel) are replaced, not added to
     st_funnel_quote     a volume order sent on to the quote form
   Every event carries event_id, niche, route and currency; values are in the currency's units. */
if (!customElements.get('st-funnel')) {
  customElements.define(
    'st-funnel',
    class extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        const json = (sel) => {
          try {
            return JSON.parse(this.querySelector(sel).textContent);
          } catch (e) {
            return null;
          }
        };
        this.cat = json('[data-st-fn-data]');
        this.cfg = json('[data-st-fn-config]');
        if (!this.cat || !this.cfg || !window.stFunnelRules) return;
        for (const f of Object.keys(this.cfg.packs)) {
          const list = String(this.cfg.packs[f]).split(',').map((n) => parseInt(n, 10)).filter((n) => n > 0);
          while (list.length < 4) list.push(list[list.length - 1] || 1);
          this.cfg.packs[f] = list;
        }
        this.s = {};
        this.querySelector('[data-st-fn-tpl="strings"]').content.querySelectorAll('[data-k]').forEach((el) => (this.s[el.dataset.k] = el.textContent.trim()));
        this.form = this.querySelector('[data-st-fn-form]');
        this.nextBar = this.querySelector('[data-st-fn-nextbar]');
        this.nextBtn = this.querySelector('[data-st-fn-next]');
        this.backBtn = this.querySelector('[data-st-fn-back]');
        this.head = this.querySelector('[data-st-fn-head]');
        this.money = this.moneyFormat(this.cat.money);
        this.store = 'st-funnel';
        this.started = false;
        this.pointer = false;
        this.tier = 'recommended';

        const params = new URLSearchParams(location.search);
        this.utm = {};
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach((k) => params.get(k) && (this.utm[k] = params.get(k).slice(0, 100)));
        this.entry = this.dataset.entry || '';

        this.restore();
        if (this.audienceInput()) this.prefill(this.audienceInput());
        this.addEventListener('pointerdown', () => (this.pointer = true), true);
        this.addEventListener('keydown', () => (this.pointer = false), true);
        this.form.addEventListener('change', (e) => this.onChange(e));
        this.form.addEventListener('submit', (e) => e.preventDefault());
        this.nextBtn.addEventListener('click', () => this.next());
        this.backBtn.addEventListener('click', () => this.back());
        this.querySelectorAll('[data-st-fn-restart]').forEach((b) => b.addEventListener('click', () => this.go(this.steps()[0])));
        this.querySelector('[data-st-fn-tiers]').addEventListener('change', (e) => this.pickTier(e.target.value));
        this.form.addEventListener('click', (e) => this.onClick(e));
        this.wide = window.matchMedia('(min-width: 900px)');
        this.wide.addEventListener('change', () => this.placeFooters());
        window.addEventListener('popstate', (e) => e.state && e.state.stFn && this.go(e.state.stFn, false));
        // Back from checkout: the page can come back from the browser's cache with a button still busy.
        // Safari restores the page with its pack chips unticked (seen on iOS, 2026-10-11): their
        // defaults are kept in step with their state (see paint), and every row is redrawn from the
        // funnel's own state, again a frame later in case the restore lands after this event.
        window.addEventListener('pageshow', (e) => {
          if (!e.persisted) return;
          this.querySelectorAll('[data-f="get"]').forEach((b) => {
            b.disabled = false;
            if (b.dataset.label) b.textContent = b.dataset.label;
          });
          const redraw = () => this.querySelectorAll('.st-fn__item').forEach((li) => li.stFnPaint && li.stFnPaint());
          redraw();
          requestAnimationFrame(redraw);
        });

        const first = this.at && this.steps().includes(this.at) ? this.at : this.visibleStep();
        history.replaceState({ ...(history.state || {}), stFn: first, stFnDepth: 0 }, '');
        this.go(first, false, true);
        this.publish('st_funnel_view', { ...this.utm, entry: this.entry });
      }

      /* ---------- state ---------- */
      val(name) {
        const el = this.form.querySelector(`input[name="${name}"]:checked`);
        return el ? el.value : '';
      }
      vals(name) {
        return [...this.form.querySelectorAll(`input[name="${name}"]:checked`)].map((i) => i.value);
      }
      audienceInput() {
        return this.form.querySelector('input[name="audience"]:checked');
      }
      route() {
        const a = this.audienceInput();
        if (a && a.dataset.route === 'volume') return 'volume';
        return this.val('size') === '4' ? 'volume' : 'shop';
      }
      steps() {
        if (this.route() === 'volume') {
          const fromSize = this.val('size') === '4' && !(this.audienceInput() && this.audienceInput().dataset.route === 'volume');
          return fromSize
            ? ['audience', 'platforms', 'spots', 'size', 'locations', 'formats', 'units', 'design', 'volume']
            : ['audience', 'locations', 'formats', 'platforms', 'units', 'design', 'volume'];
        }
        return ['audience', 'platforms', 'spots', 'size', 'result'];
      }
      visibleStep() {
        const shown = [...this.querySelectorAll('[data-step]')].find((s) => !s.hidden);
        return shown ? shown.dataset.step : 'audience';
      }
      answered(step) {
        const multi = { platforms: 'platforms', spots: 'spots', formats: 'formats' };
        if (multi[step]) return this.vals(multi[step]).length > 0 || step === 'spots';
        return !!this.val(step);
      }
      save() {
        const data = { at: this.at, entry: this.entry, checked: [...this.form.querySelectorAll('input:checked')].map((i) => `${i.name}=${i.value}`) };
        try {
          sessionStorage.setItem(this.store, JSON.stringify(data));
        } catch (e) {}
      }
      restore() {
        try {
          const data = JSON.parse(sessionStorage.getItem(this.store) || 'null');
          if (!data || (this.entry && data.entry !== this.entry)) return;
          data.checked.forEach((pair) => {
            const [name, value] = pair.split('=');
            const input = [...this.form.querySelectorAll(`input[name="${name}"]`)].find((i) => i.value === value);
            if (input) input.checked = true;
          });
          this.at = data.at;
          this.started = true;
        } catch (e) {}
      }

      /* ---------- navigation ---------- */
      go(step, push = true, initial = false) {
        if (!step) return;
        const steps = this.steps();
        if (!steps.includes(step)) step = steps[0];
        if (step === 'result') this.renderShop();
        if (step === 'volume') this.renderVolume();
        this.at = step;
        this.querySelectorAll('[data-step]').forEach((s) => (s.hidden = s.dataset.step !== step));
        const i = steps.indexOf(step);
        const questions = steps.length - 1;
        const isResult = step === 'result' || step === 'volume';
        const firstShown = i === 0 || (this.entry && i === 1 && steps[0] === 'audience');
        this.head.hidden = !firstShown;
        this.backBtn.hidden = i === 0 || (this.entry && i === 1);
        // A pick-one question moves on when tapped, so Next shows there only once something is
        // chosen (a keyboard user, or someone who came back); a pick-any question always has it.
        const pickOne = !!this.querySelector(`[data-step="${step}"] input[type="radio"]`);
        this.nextBar.hidden = isResult || (pickOne && !this.answered(step));
        this.nextBtn.disabled = !this.answered(step);
        const count = this.querySelector('[data-st-fn-count]');
        count.textContent = isResult ? '' : this.s.step.replace('__N__', i + 1).replace('__T__', questions);
        // The 100+ answer turns four questions into eight, so going forward the line never shrinks.
        let progress = isResult ? 1 : i / questions;
        if (push === true && this.progress && progress < this.progress) progress = this.progress;
        this.progress = progress;
        this.querySelector('[data-st-fn-bar]').style.transform = `scaleX(${progress})`;
        if (push === 'replace') history.replaceState({ ...(history.state || {}), stFn: step }, '');
        else if (push) history.pushState({ ...(history.state || {}), stFn: step, stFnDepth: ((history.state && history.state.stFnDepth) || 0) + 1 }, '');
        this.save();
        if (!initial) {
          const target = this.querySelector(`[data-step="${step}"] .st-fn__legend`);
          window.scrollTo({ top: 0 });
          if (target) target.focus({ preventScroll: true });
        }
      }
      // Back steps through the funnel's own history; at its first entry (a page reloaded, or reached
      // again from elsewhere) it moves to the previous question in place rather than leaving the page.
      back() {
        if (history.state && history.state.stFnDepth > 0) return history.back();
        const steps = this.steps();
        const i = steps.indexOf(this.at);
        if (i > 0) this.go(steps[i - 1], 'replace');
      }
      next() {
        const steps = this.steps();
        const i = steps.indexOf(this.at);
        if (i < 0 || !this.answered(this.at)) return;
        this.go(steps[i + 1]);
      }

      onClick(e) {
        const input = e.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !this.pointer) return;
        const stepEl = input.closest('[data-step]');
        if (!stepEl || stepEl.classList.contains('st-fn__step--result')) return;
        this.changed = false;
        const step = stepEl.dataset.step;
        setTimeout(() => !this.changed && this.at === step && input.checked && this.next(), 0);
      }
      onChange(e) {
        this.changed = true;
        const input = e.target;
        if (!(input instanceof HTMLInputElement)) return;
        // Only the questions; a result's own controls (tier, packs, metal) handle themselves, and a pack
        // chip has already been redrawn, so detached, by the time its change reaches the form.
        const stepEl = input.closest('[data-step]');
        if (!stepEl || stepEl.classList.contains('st-fn__step--result')) return;
        const step = stepEl.dataset.step;
        if (input.name === 'audience') this.prefill(input);
        if (input.name === 'spots' && this.route() === 'volume') this.syncFormats();
        if (!this.started) {
          this.started = true;
          this.publish('st_funnel_start', { step });
        }
        this.publish('st_funnel_step', { step, answer: input.type === 'checkbox' ? this.vals(input.name).join(',') : input.value });
        this.nextBtn.disabled = !this.answered(step);
        if (input.type === 'radio' && !this.pointer) this.nextBar.hidden = false;
        this.save();
        if (input.type === 'radio' && this.pointer) setTimeout(() => this.at === step && this.next(), 180);
      }
      // The audience's likely platforms are ticked first, unless the shopper has picked some.
      prefill(audience) {
        const pre = (audience.dataset.platforms || '').split(',').map((p) => p.trim()).filter(Boolean);
        if (pre.length && !this.vals('platforms').length) {
          this.form.querySelectorAll('input[name="platforms"]').forEach((p) => (p.checked = pre.includes(p.value)));
        }
      }
      // Spots answered before a size of 100+ carry over as the volume route's products.
      syncFormats() {
        const formats = new Set([...this.form.querySelectorAll('input[name="spots"]:checked')].map((i) => i.dataset.format));
        this.form.querySelectorAll('input[name="formats"]').forEach((f) => formats.has(f.value) && (f.checked = true));
      }

      /* ---------- shop result ---------- */
      answers() {
        const route = this.route();
        if (route === 'volume') {
          const u = this.val('units');
          return { route, platforms: this.vals('platforms'), formats: this.vals('formats'), units: u === 'more' ? 'more' : parseInt(u, 10), design: this.val('design') };
        }
        const formats = [...this.form.querySelectorAll('input[name="spots"]:checked')].map((i) => i.dataset.format);
        return { route, platforms: this.vals('platforms'), formats, band: parseInt(this.val('size') || '0', 10) };
      }
      renderShop() {
        if (this.route() !== 'shop') return;
        const ans = this.answers();
        if (!ans.platforms.length) ans.platforms = [this.cat.platforms[0] && this.cat.platforms[0].key].filter(Boolean);
        this.result = window.stFunnelRules.build(ans, this.cat, this.cfg);
        this.renderNotes(this.querySelector('[data-st-fn-notes]'), this.result.notes);
        const wrap = this.querySelector('[data-st-fn-setups]');
        wrap.replaceChildren();
        this.cards = {};
        this.result.setups.forEach((setup) => {
          if (!setup.items.length) return;
          setup.maxPack = 50;
          const card = this.renderSetup(setup);
          this.cards[setup.tier] = card;
          wrap.append(card);
        });
        const tiers = this.querySelector('[data-st-fn-tiers]');
        tiers.querySelectorAll('input').forEach((i) => (i.closest('label').hidden = !this.cards[i.value]));
        if (!this.cards[this.tier]) this.tier = Object.keys(this.cards)[0];
        this.pickTier(this.tier, false);
        this.renderEcho();
        this.placeFooters();
        this.watchFooter();
        const key = JSON.stringify(ans);
        if (key === this.lastResult) return;
        this.lastResult = key;
        this.publish('st_funnel_result', {
          route: 'shop',
          setups: this.result.setups.map((s) => ({ tier: s.tier, value: this.units(s.total), items: s.items.map((i) => ({ product: i.h, variant: i.id, pack: i.n, price: this.units(i.price) })) })),
          value: this.units((this.result.setups.find((s) => s.tier === 'recommended') || this.result.setups[0]).total),
        });
      }
      // The answers the setups were built from, as pills above them.
      renderEcho() {
        const list = this.querySelector('[data-st-fn-echo]');
        list.replaceChildren();
        this.answered_().forEach(({ a }) => a.forEach((text) => {
          const li = document.createElement('li');
          li.className = 'st-fn__pill';
          li.textContent = text;
          list.append(li);
        }));
      }
      // [{q, a: [labels]}] for the steps of the current route that have an answer.
      answered_() {
        const out = [];
        this.steps().forEach((step) => {
          const sec = this.querySelector(`[data-step="${step}"]`);
          if (!sec || sec.classList.contains('st-fn__step--result')) return;
          const legend = sec.querySelector('legend');
          if (!legend) return;
          const a = [...sec.querySelectorAll('input:checked')].map((i) => i.closest('label').querySelector('.st-fn__tile-t').firstChild.textContent.trim());
          if (a.length) out.push({ q: legend.firstChild.textContent.trim(), a });
        });
        return out;
      }
      // On a wide screen each card leads with its total and button; on a phone they stay last, held
      // at the foot of the screen.
      placeFooters() {
        this.querySelectorAll('.st-fn__setup').forEach((card) => {
          const foot = card.querySelector('.st-fn__setup-f');
          if (!foot) return;
          if (this.wide && this.wide.matches) card.querySelector('.st-fn__setup-h').after(foot);
          else card.append(foot);
        });
      }
      renderNotes(box, notes) {
        box.replaceChildren();
        notes.forEach((n) => {
          const p = document.createElement('p');
          p.className = 'st-fn__note';
          if (n.more) {
            p.textContent = `${this.s.more_stand} `;
            p.append(this.quoteLink());
          } else {
            const platform = (this.cat.platforms.find((x) => x.key === n.platform) || {}).label || n.platform;
            p.textContent = this.s.swap.replace('__PL__', platform).replace('__F__', this.lower(this.s[`fmt_${n.from}`])).replace('__T__', this.lower(this.s[`fmt_${n.to}`]));
          }
          box.append(p);
        });
      }
      lower(text) {
        return (text || '').toLocaleLowerCase(document.documentElement.lang || undefined);
      }
      quoteLink() {
        const a = document.createElement('a');
        a.href = this.dataset.quote;
        a.textContent = this.s.quote;
        a.addEventListener('click', () => this.toQuote());
        return a;
      }
      renderSetup(setup) {
        const card = this.querySelector('[data-st-fn-tpl="setup"]').content.firstElementChild.cloneNode(true);
        const f = (k) => card.querySelector(`[data-f="${k}"]`);
        card.dataset.tier = setup.tier;
        f('tier').textContent = this.s[`tier_${setup.tier}`];
        const code = this.cfg.codes[setup.tier];
        if (code && setup.pct > 0) {
          f('badge').hidden = false;
          f('badge').textContent = this.s.discount.replace('__P__', setup.pct);
        }
        setup.items.forEach((item, idx) => f('items').append(this.renderItem(setup, item, idx, card)));
        f('get').addEventListener('click', () => this.checkout(setup, card));
        this.updateTotal(setup, card);
        return card;
      }
      renderItem(setup, item, idx, card) {
        const li = this.querySelector('[data-st-fn-tpl="item"]').content.firstElementChild.cloneNode(true);
        const f = (k) => li.querySelector(`[data-f="${k}"]`);
        let metalLabel = null;
        const descId = `st-fn-d-${Math.random().toString(36).slice(2, 9)}`;
        f('desc').id = descId;
        f('main').setAttribute('aria-controls', descId);
        const paint = (focusId) => {
          const cur = setup.items[idx];
          f('img').src = cur.img || '';
          f('img').hidden = !cur.img;
          f('name').textContent = cur.title;
          // "$131.95" alone reads as one stand; a pack says what each one costs.
          f('price').textContent = cur.n > 1 ? `${this.fmt(cur.price)} · ${this.s.each.replace('__P__', this.fmt(Math.round(cur.price / cur.n)))}` : this.fmt(cur.price);
          f('desc').textContent = this.s[`fmt_${cur.format}_d`] || '';
          f('metal-in').checked = f('metal-in').defaultChecked = cur.format === 'metal';
          if (metalLabel) metalLabel();
          const chips = f('packs');
          chips.replaceChildren();
          const name = `pk-${setup.tier}-${idx}-${Math.random().toString(36).slice(2, 7)}`;
          const packs = cur.packs.filter((p) => p.n >= (setup.minPack || 0) && p.n <= (setup.maxPack || Infinity));
          packs.forEach((p) => {
            const label = document.createElement('label');
            label.className = 'st-fn__chip';
            const input = document.createElement('input');
            input.type = 'radio';
            input.name = name;
            input.value = p.id;
            // The default too, not only the state: WebKit resets this form's controls to their
            // defaults when it brings the page back from its cache (seen on iOS, 2026-10-11).
            input.checked = input.defaultChecked = p.id === cur.id;
            input.addEventListener('change', () => {
              Object.assign(cur, { id: p.id, n: p.n, price: p.price });
              paint(p.id);
              this.updateTotal(setup, card);
              this.publish('st_funnel_pack', { tier: setup.tier, product: cur.h, variant: p.id, pack: p.n, value: this.units(p.price) });
            });
            const span = document.createElement('span');
            span.textContent = p.n;
            label.append(input, span);
            chips.append(label);
          });
          // The chips are redrawn, so a keyboard user's focus goes back to the one just picked.
          if (focusId) chips.querySelector(`input[value="${focusId}"]`)?.focus();
        };
        paint();
        li.stFnPaint = () => paint();
        f('main').addEventListener('click', () => {
          const open = f('main').getAttribute('aria-expanded') !== 'true';
          f('main').setAttribute('aria-expanded', String(open));
          f('desc').hidden = !open;
          const cur = setup.items[idx];
          this.publish('st_funnel_product', { tier: setup.tier, product: cur.h, variant: cur.id, pack: cur.n, price: this.units(cur.price), open });
        });
        const base = setup.items[idx];
        if (base.upgrade) {
          const plain = { ...base };
          const metal = { ...base.upgrade };
          f('metal').hidden = false;
          metalLabel = () => {
            const n = setup.items[idx].n;
            const diff = (metal.packs.find((p) => p.n === n) || metal).price - (plain.packs.find((p) => p.n === n) || plain).price;
            f('metal-t').textContent = this.s.metal.replace('__P__', this.fmt(Math.max(0, diff)));
          };
          metalLabel();
          f('metal-in').addEventListener('change', (e) => {
            const n = setup.items[idx].n;
            const src = e.target.checked ? metal : plain;
            const pack = src.packs.find((p) => p.n === n) || src.packs[0];
            setup.items[idx] = { ...src, upgrade: base.upgrade, id: pack.id, n: pack.n, price: pack.price };
            paint();
            this.updateTotal(setup, card);
            const cur = setup.items[idx];
            this.publish('st_funnel_setup', { tier: setup.tier, metal: e.target.checked, value: this.units(this.sum(setup)) });
            this.publish('st_funnel_product', { tier: setup.tier, product: cur.h, variant: cur.id, pack: cur.n, price: this.units(cur.price) });
          });
        }
        return li;
      }
      // "10 stands, 10 cards and 1 stand for Tripadvisor": what a setup holds, in units.
      covers(setup) {
        const primary = this.result && this.result.primary;
        const parts = setup.items.map((i) => {
          const unit = this.s[`unit_${i.format}_${i.n === 1 ? 'one' : 'other'}`] || '';
          const platform = (this.cat.platforms.find((x) => x.key === i.platform) || {}).label || i.platform;
          return (primary && i.platform !== primary ? this.s.for_platform.replace('__PL__', platform) : this.s.count).replace('__N__', i.n).replace('__U__', unit);
        });
        return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} ${this.s.and} ${parts[parts.length - 1]}` : parts.join('');
      }
      sum(setup) {
        return setup.items.reduce((t, i) => t + i.price, 0);
      }
      updateTotal(setup, card) {
        const f = (k) => card.querySelector(`[data-f="${k}"]`);
        const total = this.sum(setup);
        const off = this.cfg.codes[setup.tier] && setup.pct > 0 ? Math.round((total * setup.pct) / 100) : 0;
        f('now').textContent = this.fmt(total - off);
        f('was').hidden = !off;
        if (off) f('was').textContent = `${this.s.was} ${this.fmt(total)}`;
        f('covers').textContent = this.covers(setup);
        const tab = this.querySelector(`[data-st-fn-tier-total="${setup.tier}"]`);
        if (tab) tab.textContent = this.fmt(total - off);
      }
      watchFooter() {
        if (!window.ResizeObserver) return;
        this.footObs?.disconnect();
        const set = () => {
          const h = Math.max(0, ...[...this.querySelectorAll('.st-fn__setup.is-picked .st-fn__setup-f')].map((el) => el.offsetHeight));
          document.documentElement.style.setProperty('--st-fn-foot', `${h}px`);
        };
        this.footObs = new ResizeObserver(set);
        this.querySelectorAll('.st-fn__setup-f').forEach((el) => this.footObs.observe(el));
        set();
      }
      pickTier(tier, track = true) {
        if (!this.cards || !this.cards[tier]) return;
        this.tier = tier;
        Object.entries(this.cards).forEach(([t, card]) => card.classList.toggle('is-picked', t === tier));
        if (this.footObs) this.watchFooter();
        const input = this.querySelector(`[data-st-fn-tiers] input[value="${tier}"]`);
        if (input) {
          this.querySelectorAll('[data-st-fn-tiers] input').forEach((i) => (i.defaultChecked = i === input));
          input.checked = true;
        }
        if (track) {
          const setup = this.result.setups.find((s) => s.tier === tier);
          this.publish('st_funnel_setup', { tier, value: this.units(this.sum(setup)) });
        }
      }

      /* ---------- volume result ---------- */
      renderVolume() {
        const ans = this.answers();
        const r = window.stFunnelRules.build(ans, this.cat, this.cfg);
        this.vresult = r;
        this.renderNotes(this.querySelector('[data-st-fn-vnotes]'), r.notes);
        const box = this.querySelector('[data-st-fn-vol]');
        box.replaceChildren();
        if (r.items.length) {
          const setup = { tier: 'volume', items: r.items, pct: 0, total: r.total, minPack: 100 };
          const card = this.renderSetup(setup);
          card.querySelector('[data-f="tier"]').textContent = '';
          card.querySelector('[data-f="get"]').textContent = this.s.buy;
          card.classList.add('is-picked');
          box.append(card);
          this.placeFooters();
        }
        if (r.quote) {
          const panel = document.createElement('div');
          panel.className = 'st-fn__quote';
          const h = document.createElement('h3');
          h.className = 'st-fn__quote-h';
          h.textContent = this.s.vol_quote_h;
          panel.append(h);
          r.reasons.forEach((reason) => {
            const p = document.createElement('p');
            if (reason === 'pack') {
              const names = [...new Set(r.missing.map((m) => this.s[`fmt_${m.format}`]))].join(', ');
              p.textContent = this.s.reason_pack.replace('__I__', names);
            } else p.textContent = this.s[`reason_${reason}`];
            panel.append(p);
          });
          const a = this.quoteLink();
          a.textContent = this.s.quote_send;
          a.className = `st-btn ${r.items.length ? 'st-btn--sec' : 'st-btn--pri'}`;
          panel.append(a);
          box.append(panel);
        }
        const brief = this.querySelector('[data-st-fn-brief]');
        brief.hidden = !r.quote;
        const dl = this.querySelector('[data-st-fn-brief-l]');
        dl.replaceChildren();
        this.answered_().forEach(({ q, a }) => {
          const row = document.createElement('div');
          const dt = document.createElement('dt');
          const dd = document.createElement('dd');
          dt.textContent = q;
          dd.textContent = a.join(', ');
          row.append(dt, dd);
          dl.append(row);
        });
        this.publish('st_funnel_result', {
          route: 'volume',
          value: this.units(r.total),
          items: r.items.map((i) => ({ product: i.h, variant: i.id, pack: i.n, price: this.units(i.price) })),
          quote: r.quote,
          reasons: r.reasons.join(','),
        });
      }
      brief() {
        return [this.s.brief, ...this.answered_().map(({ q, a }) => `${q} ${a.join(', ')}`)].join('\n');
      }
      toQuote() {
        try {
          sessionStorage.setItem('st-funnel-brief', this.brief());
        } catch (e) {}
        this.publish('st_funnel_quote', { reasons: this.vresult ? this.vresult.reasons.join(',') : '' });
      }

      /* ---------- checkout ---------- */
      async checkout(setup, card) {
        const btn = card.querySelector('[data-f="get"]');
        const err = card.querySelector('[data-f="err"]');
        const label = btn.textContent;
        btn.dataset.label = label;
        btn.disabled = true;
        btn.textContent = this.s.getting;
        err.hidden = true;
        const code = this.cfg.codes[setup.tier] || '';
        const value = this.sum(setup);
        try {
          const post = (url, body) =>
            fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }).then((r) => {
              if (!r.ok) throw new Error(String(r.status));
              return r.json();
            });
          // Lines this funnel added before (an earlier setup, a retry) are taken out first, so the cart
          // holds exactly the setup on screen; anything else in the cart is left alone.
          const cart = await fetch(`${this.cat.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
          const old = {};
          cart.items.forEach((line) => line.properties && line.properties._st_funnel && (old[line.key] = 0));
          if (Object.keys(old).length) await post(this.cat.routes.update, { updates: old });
          await post(this.cat.routes.add, {
            items: setup.items.map((i) => ({ id: i.id, quantity: 1, properties: { _st_funnel: setup.tier, ...globalThis.stFunnelRules.propertiesFor(this.cat, i.id) } })),
          });
          const attributes = { _st_funnel_route: this.route(), _st_funnel_niche: this.val('audience') || this.entry, _st_funnel_tier: setup.tier };
          Object.entries(this.utm).forEach(([k, v]) => (attributes[`_st_${k}`] = v));
          await post(this.cat.routes.update, code ? { attributes, discount: code } : { attributes });
          this.publish('st_funnel_checkout', {
            tier: setup.tier,
            value: this.units(value),
            code,
            items: setup.items.map((i) => ({ product: i.h, variant: i.id, pack: i.n, price: this.units(i.price) })),
          });
          const form = document.createElement('form');
          form.method = 'post';
          form.action = this.cat.routes.cart;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'checkout';
          input.value = '1';
          form.append(input);
          document.body.append(form);
          form.submit();
        } catch (e) {
          btn.disabled = false;
          btn.textContent = label;
          err.textContent = this.s.error;
          err.hidden = false;
        }
      }

      /* ---------- money and events ---------- */
      // The store's money filter printed 1,234,567.89 (or its own way); find the separators and the
      // number's place in it, then print any amount the same way.
      moneyFormat(sample) {
        const m = (sample || '').match(/\d[\d.,'\s  ]*\d/);
        if (!m) return { pre: '$', post: '', dec: '.', thou: ',', places: 2 };
        const num = m[0];
        const tail = num.match(/([.,])(\d{2})$/);
        const places = tail && num.replace(/\D/g, '').length === 9 ? 2 : 0;
        const dec = places ? tail[1] : '.';
        const body = places ? num.slice(0, -3) : num;
        const thouMatch = body.match(/\d([^\d])\d{3}$/);
        return { pre: sample.slice(0, m.index), post: sample.slice(m.index + num.length), dec, thou: thouMatch ? thouMatch[1] : '', places };
      }
      fmt(cents) {
        const m = this.money;
        const value = m.places ? (cents / 100).toFixed(2) : String(Math.round(cents / 100));
        const [whole, frac] = value.split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, m.thou);
        return `${m.pre}${grouped}${frac ? m.dec + frac : ''}${m.post}`;
      }
      units(cents) {
        return Math.round(cents) / 100;
      }
      publish(name, data = {}) {
        try {
          const id = window.crypto && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          window.Shopify?.analytics?.publish?.(name, {
            event_id: id,
            page: location.pathname,
            niche: this.val('audience') || this.entry,
            route: this.route(),
            currency: this.cat.currency,
            ...data,
          });
          this.upstackView(name, data);
        } catch (e) {}
      }
      // The funnel's ViewContent rows (TRACKING.md) to Meta through Upstack (assets/st-upstack.js): the
      // result at the Recommended setup's value, a product or a pack at its own.
      upstackView(name, data) {
        const pid = (h) => this.cat.products.find((p) => p.h === h)?.pid;
        const item = (product, variant, price, pack) => ({ productId: pid(product), variant, price, name: this.itemName(product, pack) });
        let value;
        let items;
        if (name === 'st_funnel_result') {
          // Shop route: the Recommended setup; volume route: its one list of packs.
          const s = data.setups ? data.setups.find((x) => x.tier === 'recommended') || data.setups[0] : data;
          if (!s?.items?.length) return;
          value = s.value;
          items = s.items.map((i) => item(i.product, i.variant, i.price, i.pack));
        } else if (name === 'st_funnel_product' || name === 'st_funnel_pack') {
          if (data.open === false) return; // closing a product row is not a view
          value = name === 'st_funnel_pack' ? data.value : data.price;
          items = [item(data.product, data.variant, value, data.pack)];
        } else return;
        const send = () => window.stUpstackView?.({ currency: this.cat.currency, value, items });
        if (name !== 'st_funnel_pack') return send();
        // Pack chips settle as on the product page (assets/st-track.js): one view, 1 second after the last
        // tap on that setup's product.
        this.packTimers ||= new Map();
        const key = `${data.tier}|${data.product}`;
        clearTimeout(this.packTimers.get(key));
        this.packTimers.set(key, setTimeout(send, 1000));
      }
      itemName(handle, pack) {
        const title = this.cat.products.find((p) => p.h === handle)?.title;
        return title ? `${title} - ${pack}` : pack || '';
      }
    }
  );
}
